import { useEffect, useMemo, useState } from 'react';
import {
  FileText, Calculator, Sparkles, Download, Eye, Save, History, GanttChartSquare,
} from 'lucide-react';
import {
  useAuditReport,
  useAuditReportRealtime,
  useCreateReportSnapshot,
  useScorecard,
  type ReportSection,
} from '../../../lib/api/queries';
import { NarrativeSectionEditor } from './NarrativeSectionEditor';
import { ComputedSectionView } from './ComputedSectionView';
import {
  SectionEvidenceTable,
  evidenceForSectionKey,
  type EvidenceRow,
} from './EvidenceTable';
import { evidenceDownloadUrl } from '../../../lib/api/queries';
import { ActivityFeed } from './ActivityFeed';
import { ReportPreviewModal } from './ReportPreviewModal';
import { VersionListModal } from './VersionListModal';
import { BAND_COLORS } from '../ScorecardView';
import {
  hasFormNotes,
  readDrawerOpen,
  useNoteAttention,
  writeDrawerOpen,
} from '../../../lib/api/formNotes';

/**
 * /audits/:id "Report" tab. Two-pane layout:
 *
 *  - Left: ordered list of all report sections (cover, narrative blocks,
 *          computed sections, closing statement) with a small icon for
 *          each indicating Narrative vs Computed.
 *  - Right: the active section. Narrative sections show the Tiptap
 *          editor + Save button + version history dropdown. Computed
 *          sections show a live read-only render so the auditor can
 *          see what the PDF will contain.
 *
 * The Download PDF button at the top reuses the existing
 * <c>/api/audit/audits/{id}/report/pdf</c> endpoint.
 */
export function ReportView({
  tenantId,
  auditId,
  editable,
}: {
  tenantId: string;
  auditId: string;
  editable: boolean;
}) {
  const { data: report, isLoading, error } = useAuditReport(tenantId, auditId);
  const { data: scorecard } = useScorecard(tenantId, auditId);
  // Live updates: a co-auditor saving a narrative or capturing a snapshot
  // invalidates the relevant queries so this tab reflects the change
  // without a manual refresh.
  useAuditReportRealtime(tenantId, auditId);
  const [activeKey, setActiveKey] = useState<string | null>(null);
  const [previewing, setPreviewing] = useState(false);
  const [showingVersions, setShowingVersions] = useState(false);
  const [justSaved, setJustSaved] = useState<string | null>(null);
  const createSnapshot = useCreateReportSnapshot(tenantId, auditId);

  // Slice 19c — Form notes drawer. Closed by default; the choice is
  // remembered per browser. While it's open it takes the activity feed's
  // column so the page doesn't grow a fourth one.
  const [notesOpen, setNotesOpen] = useState(readDrawerOpen);
  const setNotes = (open: boolean) => { setNotesOpen(open); writeDrawerOpen(open); };
  const attention = useNoteAttention(tenantId, auditId).data ?? {};

  // Map audit-section id (dashless "N" format, matching how the section
  // key is composed on the backend) → traffic-light colour. Used by the
  // sidebar to dot each section_narrative_* entry with its current band
  // so the auditor sees at a glance which sections need attention while
  // writing.
  const bandColourBySectionId = useMemo(() => {
    const map = new Map<string, string>();
    scorecard?.sections.forEach(s => {
      map.set(s.sectionId.replace(/-/g, ''), BAND_COLORS[s.band]);
    });
    return map;
  }, [scorecard]);

  // Overall audit band — dot on the four "summary" computed sections
  // (Scorecard, Heatmap, Action Prioritisation, Timeline) so the reader
  // sees the aggregate state at a glance even for sections that aren't
  // pinned to a single audit section.
  const overallBandColour = scorecard ? BAND_COLORS[scorecard.overall.band] : undefined;

  // Slice 17 — the report's evidence, flattened to the shape the shared
  // table renders. Done once here rather than per section so the preview,
  // the register and the client viewer all read from one normalisation.
  const evidenceRows = useMemo<EvidenceRow[]>(
    () => (report?.evidence ?? []).map(e => ({
      id:            e.id,
      sectionId:     e.sectionId,
      sectionCode:   e.sectionCode,
      criterionCode: e.criterionCode,
      itemText:      e.itemText,
      referenceCode: e.referenceCode,
      fileName:      e.fileName,
      sizeBytes:     e.sizeBytes,
      description:   e.description,
      itemComment:   e.itemComment,
      uploadedAtUtc: e.uploadedAtUtc,
      blocked:       e.scanStatus === 'Infected',
      href:          evidenceDownloadUrl(auditId, e.id),
    })),
    [report, auditId],
  );

  const onSaveVersion = async () => {
    const label = window.prompt(
      'Label for this version (optional — e.g. "v1.0 sent to client")') ?? null;
    // The user pressing Cancel returns null — we still capture the version
    // with no label so the click can't be lost.
    const meta = await createSnapshot.mutateAsync({ label });
    setJustSaved(meta.label ?? `Version saved ${new Date(meta.createdAtUtc).toLocaleString()}`);
    window.setTimeout(() => setJustSaved(null), 4000);
  };

  // Default-select the first narrative section once data lands so the
  // editor isn't empty on first visit. If none exists, fall back to the
  // first section overall.
  useEffect(() => {
    if (activeKey || !report) return;
    const firstNarrative = report.sections.find(s => s.kind === 'Narrative');
    setActiveKey((firstNarrative ?? report.sections[0])?.key ?? null);
  }, [report, activeKey]);

  const activeSection = useMemo(
    () => report?.sections.find(s => s.key === activeKey) ?? null,
    [report, activeKey]);

  if (isLoading) return <p className="text-sm text-gray-500">Loading report…</p>;
  if (error)     return <p className="text-sm text-danger-500">Couldn't load the report.</p>;
  if (!report)   return null;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3">
        <p className="text-xs text-gray-500">
          Narrative sections you write here are version-historied; computed
          sections refresh from the audit form every time you load this tab.
        </p>
        <div className="flex items-center gap-2 shrink-0">
          <button type="button"
                  onClick={onSaveVersion}
                  disabled={!editable || createSnapshot.isPending}
                  title="Capture every section's current content as a saved version"
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium
                             text-accent-alt-700 border border-accent-alt-500/40
                             hover:bg-accent-alt-50 rounded-md transition-colors
                             whitespace-nowrap disabled:opacity-40 disabled:cursor-not-allowed">
            <Save className="w-4 h-4" aria-hidden />
            {createSnapshot.isPending ? 'Saving…' : 'Save version'}
          </button>
          <button type="button"
                  onClick={() => setShowingVersions(true)}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium
                             text-accent-alt-700 border border-accent-alt-500/40
                             hover:bg-accent-alt-50 rounded-md transition-colors
                             whitespace-nowrap">
            <History className="w-4 h-4" aria-hidden /> Preview versions
          </button>
          <button type="button"
                  onClick={() => setPreviewing(true)}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium
                             text-accent-alt-700 border border-accent-alt-500/40
                             hover:bg-accent-alt-50 rounded-md transition-colors
                             whitespace-nowrap">
            <Eye className="w-4 h-4" aria-hidden /> Preview report
          </button>
          <a href={`/api/audit/audits/${auditId}/report/pdf`} download
             className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium
                        text-white bg-accent-alt-600 hover:bg-accent-alt-700 rounded-md
                        transition-colors whitespace-nowrap">
            <Download className="w-4 h-4" aria-hidden /> Download PDF
          </a>
        </div>
      </div>

      {justSaved && (
        <p className="text-xs text-success-600 -mt-2">
          ✓ Saved · {justSaved}
        </p>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-[18rem_1fr_auto] gap-6 items-start">
        <div className="lg:sticky lg:top-4">
          <SectionList sections={report.sections}
                       activeKey={activeKey}
                       onSelect={setActiveKey}
                       attention={attention}
                       bandColourBySectionId={bandColourBySectionId}
                       overallBandColour={overallBandColour} />
        </div>

        <div className="min-w-0">
          {activeSection ? (
            activeSection.kind === 'Narrative' ? (
              <>
                <NarrativeSectionEditor
                  tenantId={tenantId}
                  auditId={auditId}
                  section={activeSection}
                  editable={editable}
                  notesOpen={notesOpen}
                  onToggleNotes={() => setNotes(!notesOpen)}
                  attentionCount={attention[activeSection.key] ?? 0}
                />
                {/* Slice 17 — the same evidence table the PDF renders under
                    this section, so preview and export agree. */}
                <SectionEvidenceTable
                  rows={evidenceForSectionKey(activeSection.key, evidenceRows)}
                />
              </>
            ) : (
              <ComputedSectionView
                tenantId={tenantId}
                auditId={auditId}
                section={activeSection}
                editable={editable}
                evidenceRows={evidenceRows}
              />
            )
          ) : (
            <p className="text-sm text-gray-500">
              Pick a section from the list to start.
            </p>
          )}
        </div>

        {/* Slice 12 activity feed. Hides below `lg` — Report tab is
            information-dense enough on tablets. */}
        {!(notesOpen && activeSection && hasFormNotes(activeSection.key)) && (
          <div className="lg:sticky lg:top-4">
            <ActivityFeed tenantId={tenantId} auditId={auditId} />
          </div>
        )}
      </div>

      {previewing && (
        <ReportPreviewModal
          tenantId={tenantId}
          auditId={auditId}
          source={{ kind: 'live', report }}
          onClose={() => setPreviewing(false)}
        />
      )}

      {showingVersions && (
        <VersionListModal
          tenantId={tenantId}
          auditId={auditId}
          sections={report.sections}
          onClose={() => setShowingVersions(false)}
          onReviewPending={sectionKey => {
            // Publishing was blocked by form notes awaiting review: take
            // the author to the section with the drawer open.
            setShowingVersions(false);
            setActiveKey(sectionKey);
            setNotes(true);
          }}
        />
      )}
    </div>
  );
}

function SectionList({
  sections, activeKey, onSelect, attention, bandColourBySectionId, overallBandColour,
}: {
  sections: ReportSection[];
  activeKey: string | null;
  onSelect: (key: string) => void;
  /** Section key → form notes needing attention (Slice 19c). */
  attention: Record<string, number>;
  bandColourBySectionId: Map<string, string>;
  overallBandColour: string | undefined;
}) {
  return (
    // Sticky beside the editor and scrolls on its own, so a long template's
    // sections are all reachable without scrolling the page.
    <nav className="bg-white border border-gray-200 rounded-lg overflow-hidden
                    flex flex-col lg:max-h-[calc(100vh-2rem)]">
      <p className="px-4 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wide
                    bg-gray-50 border-b border-gray-200">
        Report sections
      </p>
      <ul className="divide-y divide-gray-100 overflow-y-auto min-h-0">
        {sections.map(s => {
          const isActive = s.key === activeKey;
          const Icon = iconForSection(s);
          // For per-audit-section narratives the key is
          // "section_narrative_{sectionIdNoDashes}" — extract the id
          // and look up the current traffic-light colour for that
          // section. Non-matching sections (cover, overall summary,
          // etc.) get no dot.
          const bandDot = sectionBandColour(s, bandColourBySectionId, overallBandColour);
          return (
            <li key={s.id}>
              <button type="button"
                      onClick={() => onSelect(s.key)}
                      className={
                        'w-full text-left px-4 py-2.5 transition-colors ' +
                        'flex items-start gap-2 ' +
                        (isActive
                          ? 'bg-accent-alt-50 border-l-4 border-accent-alt-600'
                          : 'border-l-4 border-transparent hover:bg-gray-50')
                      }>
                <Icon className={'w-4 h-4 mt-0.5 shrink-0 ' +
                                 (isActive ? 'text-accent-alt-600' : 'text-gray-400')}
                      aria-hidden />
                <div className="min-w-0 flex-1">
                  <p className={'text-sm truncate ' +
                                (isActive ? 'font-medium text-accent-alt-700' : 'text-gray-800')}>
                    {s.title}
                  </p>
                  <p className="text-[10px] text-gray-500 uppercase tracking-wide">
                    {s.kind === 'Narrative'
                      ? (s.currentRevision
                          ? `Rev ${s.currentRevision}`
                          : 'Not yet written')
                      : 'Auto-computed'}
                  </p>
                </div>
                {(attention[s.key] ?? 0) > 0 && (
                  <span title={`${attention[s.key]} form note(s) to review`}
                        className="mt-0.5 min-w-5 h-5 px-1.5 rounded-full bg-amber-200 text-amber-900
                                   text-[11px] font-semibold inline-flex items-center justify-center shrink-0">
                    {attention[s.key]}
                  </span>
                )}
                {bandDot && (
                  <span title="Compliance band for this section"
                        className="w-2.5 h-2.5 mt-1.5 rounded-full shrink-0"
                        style={{ backgroundColor: bandDot }} />
                )}
              </button>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}

/**
 * Pick the traffic-light dot for a sidebar entry:
 *
 *  - per-section narrative → that section's band colour
 *  - summary computed section (scorecard / heatmap / prioritisation /
 *    timeline) → the overall audit band
 *  - anything else (cover, cross-cutting prose) → no dot
 *
 * Returns undefined to mean "no dot".
 */
function sectionBandColour(
  section: ReportSection,
  bandColourBySectionId: Map<string, string>,
  overallBandColour: string | undefined,
): string | undefined {
  const prefix = 'section_narrative_';
  if (section.key.startsWith(prefix)) {
    return bandColourBySectionId.get(section.key.slice(prefix.length));
  }
  if (section.kind === 'Computed'
      && section.computedKind
      && SUMMARY_COMPUTED.has(section.computedKind)) {
    return overallBandColour;
  }
  return undefined;
}

/**
 * Computed sections whose sidebar dot reflects the overall audit band
 * rather than a specific per-section band. Kept as a Set so adding a
 * new "summary" kind in future is a one-line change.
 */
const SUMMARY_COMPUTED = new Set<string>([
  'scorecardtable', 'heatmap', 'actionprioritisation', 'actiontimeline',
]);

function iconForSection(s: ReportSection) {
  if (s.kind === 'Narrative') return FileText;
  if (s.computedKind === 'actionprioritisation') return Sparkles;
  if (s.computedKind === 'actiontimeline')       return GanttChartSquare;
  return Calculator;
}
